import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useEffect, useRef } from 'react';
import { standardLegend, standardTitleStyle } from '../../utils/chartFormatting';

// Register Chart.js components
Chart.register(...registerables);

interface ChartRendererSupabaseProps {
  chartData: any;
  chartOptions: any;
  height?: string;
  width?: string;
  title?: string;
  location?: string;
  chartRef?: React.RefObject<HTMLCanvasElement>;
}

export const ChartRendererSupabase: React.FC<ChartRendererSupabaseProps> = ({
  chartData,
  chartOptions,
  height = '400px',
  width = '100%',
  title,
  location,
  chartRef: externalChartRef
}) => {
  // Use a non-nullable ref type to satisfy React's ref typing for intrinsic elements
  const internalChartRef = useRef<HTMLCanvasElement>(null as unknown as HTMLCanvasElement);
  const chartRef = (externalChartRef ?? internalChartRef) as React.RefObject<HTMLCanvasElement>;
  const chartInstanceRef = useRef<Chart | null>(null);
  const isDestroyedRef = useRef<boolean>(false);


  useEffect(() => {
    if (!chartRef.current || !chartData || !chartOptions) return;

    // Destroy existing chart if it exists
    if (chartInstanceRef.current && !isDestroyedRef.current) {
      try {
        chartInstanceRef.current.destroy();
        isDestroyedRef.current = true;
      } catch (error) {
        console.warn('Chart destroy failed:', error);
      }
      chartInstanceRef.current = null;
    }

    // Reset destroyed flag
    isDestroyedRef.current = false;

    // Create new chart
    const ctx = chartRef.current.getContext('2d');
    if (ctx) {
      try {
        const legendOptions = chartOptions?.plugins?.legend
          ? {
              ...chartOptions.plugins.legend,
              position: standardLegend.position,
              labels: {
                ...standardLegend.labels,
                ...(chartOptions.plugins.legend.labels || {})
              }
            }
          : chartOptions?.plugins?.legend;

        const titleOptions = chartOptions?.plugins?.title
          ? {
              ...chartOptions.plugins.title,
              ...standardTitleStyle,
              font: {
                ...standardTitleStyle.font,
                ...(chartOptions.plugins.title.font || {})
              },
              padding: {
                ...standardTitleStyle.padding,
                ...(chartOptions.plugins.title.padding || {})
              }
            }
          : chartOptions?.plugins?.title;

        const newChart = new Chart(ctx, {
          type: chartOptions.type || 'line',
          data: chartData,
          options: {
            ...chartOptions,
            plugins: {
              ...(chartOptions.plugins || {}),
              ...(legendOptions ? { legend: legendOptions } : {}),
              ...(titleOptions ? { title: titleOptions } : {})
            },
            animation: false,
          }
        });
        
        chartInstanceRef.current = newChart;
      } catch (error) {
        console.warn('Chart creation failed:', error);
      }
    }

    // Cleanup function
    return () => {
      if (chartInstanceRef.current && !isDestroyedRef.current) {
        try {
          chartInstanceRef.current.destroy();
          isDestroyedRef.current = true;
        } catch (error) {
          console.warn('Chart cleanup destroy failed:', error);
        }
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, chartOptions]);

  const chartContent = (
    <div style={{ 
      width: '100%', 
      maxWidth: '100%', 
      height, 
      position: 'relative', 
      overflow: 'hidden', 
      boxSizing: 'border-box' 
    }}>
      <canvas
        ref={chartRef}
        style={{ 
          height: '100%', 
          width: '100%', 
          maxWidth: '100%', 
          display: 'block', 
          boxSizing: 'border-box' 
        }}
      />
    </div>
  );

  // Always return just the chart content - don't wrap in ChartLayout
  // ChartLayout should be handled by the parent component
  return chartContent;
};
